import i18n from "i18next";
import { initReactI18next } from "react-i18next";

const resources = {
  en: {
    translation: {
      // Navigation
      navigation: {
        home: "Home",
        serviceEcosystem: "Service Ecosystem",
        discoveryPortal: "Discovery Portal",
        productsPortfolio: "Products",
        scheduleCall: "Schedule Call",
        startDiscovery: "Start Discovery",
      },
      // Common
      comingSoon: "Coming Soon",
      learnMore: "Learn More",
      getStarted: "Get Started",
      earlyAccess: "Early Access",
      requestDemo: "Request Demo",
      signUp: "Sign Up",
      back: "Back",
      next: "Next",
      submit: "Submit",
      cancel: "Cancel",
      close: "Close",
      save: "Save",
      edit: "Edit",
      delete: "Delete",
      view: "View",
      download: "Download",

      // Homepage
      homepage: {
        title: "Transform Sprint Plans Into Production Reality",
        subtitle: "Enterprise Engineering Transformation",
        description:
          "Move beyond agile ceremonies to true ownership culture. We don't just advise—we embed with your teams to build velocity, eliminate technical debt, and create engineering flow that scales.",
        startDiscovery: "Start Discovery Process",
        watchCaseStudy: "Watch Case Study",
        enterpriseSecurity: "Enterprise Security",
        transformations: "50+ Transformations",
        industryLeaders: "Industry Leaders",

        // Hero Section
        hero: {
          badge: "Enterprise Engineering Transformation",
          title: "Transform Sprint Plans Into Production Reality",
          description:
            "Move beyond agile ceremonies to true ownership culture. We don't just advise—we embed with your teams to build velocity, eliminate technical debt, and create engineering flow that scales.",
          cta: "Start Discovery Process",
          watchVideo: "Watch Case Study",
          stats: {
            security: "Enterprise Security",
            transformations: "50+ Transformations",
            leaders: "Industry Leaders",
          },
          flow: {
            title: "Engineering Transformation Flow",
            subtitle: "From Chaos to Velocity",
            continuous: "Continuous Delivery Pipeline",
            beforeState: "Sprint Chaos",
            transformation: "SIGNA Process",
            afterState: "Production Flow",
            beforeVelocity: "30% Velocity",
            transformingLabel: "Transforming",
            afterVelocity: "90% Velocity",
            cicdFlow: "CI/CD Flow",
            trunkBased: "Trunk-Based Development",
            metrics: {
              faster: "3x Faster Delivery",
              debt: "90% Debt Reduction",
              enterprises: "50+ Enterprises",
            },
          },
        },

        // Services Preview
        services: {
          title: "Our Services",
          subtitle: "Transform your engineering organization",
          velocityEngineering: "Velocity Engineering",
          ownershipCulture: "Ownership Culture",
          architecturalModernization: "Architectural Modernization",
        },

        // Metrics
        metrics: {
          fasterDelivery: "3x Faster Delivery",
          debtReduction: "90% Debt Reduction",
          enterprises: "50+ Enterprises",
        },

        // CTA Section
        cta: {
          title: "Ready to Transform Your Engineering Organization?",
          description:
            "Schedule a discovery call to discuss your specific challenges and learn how our embedded approach can accelerate your team's transformation.",
          scheduleCall: "Schedule Discovery Call",
          startDiscovery: "Start Discovery Process",
        },
      },

      // Service Ecosystem
      serviceEcosystem: {
        title: "Specialized Engineering Transformation Services",
        subtitle: "Service Ecosystem",
        description:
          "Three focused service areas designed to transform your engineering organization from the inside out. Each service embeds our experts directly with your teams to drive measurable, sustainable change.",

        stats: {
          teamsTransformed: "Teams Transformed",
          velocityIncrease: "Avg. Velocity Increase",
          paybackPeriod: "Avg. Payback Period",
        },

        tabs: {
          servicesOverview: "Services Overview",
          getRecommendations: "Get Recommendations",
          technologyStack: "Technology Stack",
          roiCalculator: "ROI Calculator",
        },

        services: {
          velocityEngineering: {
            title: "Velocity Engineering",
            subtitle: "Sprint-to-Production Acceleration",
            description:
              "Transform your development process from sprint planning ceremonies into production-ready features. Our embedded engineers work directly with your teams to eliminate bottlenecks, reduce technical debt, and establish sustainable delivery patterns that compound over time.",
            badge: "Most Popular",
            focusArea: "Development Speed & Quality",
            avgImpact: "35% faster delivery",
          },
          ownershipCulture: {
            title: "Ownership Culture Transformation",
            subtitle: "Accountability Systems & Team Dynamics",
            description:
              "Move beyond agile ceremonies to create genuine ownership culture where teams take full accountability for outcomes. We embed cultural change agents who work with your teams to establish sustainable practices that drive both productivity and satisfaction.",
            badge: "High Impact",
            focusArea: "Team Culture & Accountability",
            avgImpact: "25% productivity gain",
          },
          architecturalModernization: {
            title: "Architectural Modernization",
            subtitle: "System Scalability & Evolution",
            description:
              "Modernize your architecture for scale and maintainability without disrupting current operations. Our principal architects work embedded with your teams to design and implement evolutionary architecture patterns that support both current needs and future growth.",
            badge: "Enterprise",
            focusArea: "Architecture & Scalability",
            avgImpact: "40% performance improvement",
          },
        },

        cta: {
          title: "Ready to Transform Your Engineering Organization?",
          description:
            "Schedule a discovery call to discuss your specific challenges and learn how our embedded approach can accelerate your team's transformation.",
          scheduleCall: "Schedule Discovery Call",
          getRecommendations: "Get Service Recommendations",
        },
      },

      // Discovery Portal
      discoveryPortal: {
        title: "Discovery Portal",
        subtitle:
          "Transform your engineering organization with our intelligent qualification system",
        description:
          "Transform your engineering organization with our intelligent qualification system. Get personalized recommendations and connect with the right expertise for your challenges.",

        valueProps: {
          intelligentAssessment: {
            title: "Intelligent Assessment",
            description:
              "Our comprehensive evaluation identifies your specific challenges and opportunities for improvement.",
          },
          personalizedRecommendations: {
            title: "Personalized Recommendations",
            description:
              "Receive customized transformation roadmaps based on your unique technical environment and goals.",
          },
          expertConsultation: {
            title: "Expert Consultation",
            description:
              "Connect with principal engineers who specialize in your specific challenges and industry.",
          },
        },

        journey: {
          title: "Your Discovery Journey",
          steps: {
            assessment: {
              title: "Assessment",
              description: "Complete our intelligent challenge assessment",
            },
            recommendations: {
              title: "Recommendations",
              description: "Receive personalized transformation roadmap",
            },
            qualification: {
              title: "Qualification",
              description: "Provide detailed technical environment information",
            },
            discoveryCall: {
              title: "Discovery Call",
              description: "Schedule consultation with our experts",
            },
          },
        },

        cta: {
          startAssessment: "Start Assessment",
          browseResources: "Browse Resources",
        },

        assessment: {
          title: "Engineering Challenge Assessment",
          description:
            "Help us understand your current situation to provide the most relevant recommendations",
        },

        qualification: {
          title: "Qualification Form",
          description:
            "Provide detailed information to help us prepare for your discovery call",
        },

        resources: {
          title: "Resource Library",
          description:
            "Access our comprehensive collection of engineering transformation resources",
        },

        trust: {
          title: "Trust & Security",
          description:
            "Your security and confidentiality are our top priorities",
        },

        footer: {
          enterpriseSecurity: "Enterprise Security",
          freeDiscoveryCall: "Free Discovery Call",
          ndaAvailable: "NDA Available",
        },
      },

      // Products page
      products: {
        title: "Products",
        subtitle:
          "Enterprise software solutions designed for continuous transformation",
        description:
          "Discover our upcoming software products that extend SIGNA's transformation methodology into powerful enterprise platforms.",

        // Enterprise Server Management
        serverManagement: {
          title: "Enterprise Server Management",
          subtitle: "Infrastructure Automation & Monitoring",
          description:
            "Comprehensive server infrastructure management with automated deployment pipelines, real-time monitoring, and predictive maintenance capabilities.",
          features: [
            "Automated deployment orchestration",
            "Real-time performance monitoring",
            "Predictive maintenance alerts",
            "Multi-cloud infrastructure support",
            "Security compliance automation",
          ],
        },

        // Custom Integrations
        customIntegrations: {
          title: "Custom Integrations",
          subtitle: "API Orchestration & Data Pipelines",
          description:
            "Seamless integration platform for connecting enterprise systems with automated data pipelines and real-time synchronization.",
          features: [
            "API orchestration framework",
            "Real-time data synchronization",
            "Custom connector development",
            "Enterprise system integration",
            "Data transformation pipelines",
          ],
        },

        // No-Code UI Platform
        noCodePlatform: {
          title: "No-Code UI Platform",
          subtitle: "Visual Application Development",
          description:
            "Drag-and-drop interface builder for creating enterprise applications without traditional coding requirements.",
          features: [
            "Visual application builder",
            "Pre-built component library",
            "Workflow automation tools",
            "Database integration",
            "Mobile-responsive design",
          ],
        },

        // Early Access
        earlyAccess: {
          title: "Get Early Access",
          description:
            "Be among the first to experience SIGNA\'s enterprise software solutions.",
          emailPlaceholder: "Enter your email address",
          companyPlaceholder: "Company name",
          submit: "Request Early Access",
        },
      },

      // Footer
      footer: {
        description:
          "Transforming engineering organizations through embedded expertise, ownership culture, and velocity-focused methodologies.",
        services: "Services",
        company: "Company",
        velocityEngineering: "Velocity Engineering",
        ownershipCulture: "Ownership Culture",
        architecturalModernization: "Architectural Modernization",
        enterpriseConsulting: "Enterprise Consulting",
        aboutUs: "About Us",
        caseStudies: "Case Studies",
        knowledgeHub: "Knowledge Hub",
        contact: "Contact",
        allRightsReserved: "All rights reserved",
      },
    },
  },
  pt: {
    translation: {
      // Navigation
      navigation: {
        home: "Início",
        serviceEcosystem: "Ecossistema de Serviços",
        discoveryPortal: "Portal de Descoberta",
        productsPortfolio: "Produtos",
        scheduleCall: "Agendar Chamada",
        startDiscovery: "Iniciar Descoberta",
      },
      // Common
      comingSoon: "Em Breve",
      learnMore: "Saiba Mais",
      getStarted: "Começar",
      earlyAccess: "Acesso Antecipado",
      requestDemo: "Solicitar Demo",
      signUp: "Inscrever-se",
      back: "Voltar",
      next: "Próximo",
      submit: "Enviar",
      cancel: "Cancelar",
      close: "Fechar",
      save: "Salvar",
      edit: "Editar",
      delete: "Excluir",
      view: "Visualizar",
      download: "Baixar",

      // Homepage
      homepage: {
        title: "Transforme Planos de Sprint em Realidade de Produção",
        subtitle: "Transformação de Engenharia Empresarial",
        description:
          "Vá além das cerimônias ágeis para uma verdadeira cultura de propriedade. Não apenas aconselhamos—nos incorporamos com suas equipes para construir velocidade, eliminar dívida técnica e criar fluxo de engenharia que escala.",
        startDiscovery: "Iniciar Processo de Descoberta",
        watchCaseStudy: "Assistir Caso de Estudo",
        enterpriseSecurity: "Segurança Empresarial",
        transformations: "50+ Transformações",
        industryLeaders: "Líderes da Indústria",

        // Hero Section
        hero: {
          badge: "Transformação de Engenharia Empresarial",
          title: "Transforme Planos de Sprint em Realidade de Produção",
          description:
            "Vá além das cerimônias ágeis para uma verdadeira cultura de propriedade. Não apenas aconselhamos—nos incorporamos com suas equipes para construir velocidade, eliminar dívida técnica e criar fluxo de engenharia que escala.",
          cta: "Iniciar Processo de Descoberta",
          watchVideo: "Assistir Caso de Estudo",
          stats: {
            security: "Segurança Empresarial",
            transformations: "50+ Transformações",
            leaders: "Líderes da Indústria",
          },
          flow: {
            title: "Fluxo de Transformação de Engenharia",
            subtitle: "Do Caos à Velocidade",
            continuous: "Pipeline de Entrega Contínua",
            beforeState: "Caos de Sprint",
            transformation: "Processo SIGNA",
            afterState: "Fluxo de Produção",
            beforeVelocity: "30% Velocidade",
            transformingLabel: "Transformando",
            afterVelocity: "90% Velocidade",
            cicdFlow: "Fluxo CI/CD",
            trunkBased: "Desenvolvimento Trunk-Based",
            metrics: {
              faster: "3x Entrega Mais Rápida",
              debt: "90% Redução de Dívida",
              enterprises: "50+ Empresas",
            },
          },
        },

        // Services Preview
        services: {
          title: "Nossos Serviços",
          subtitle: "Transforme sua organização de engenharia",
          velocityEngineering: "Engenharia de Velocidade",
          ownershipCulture: "Cultura de Propriedade",
          architecturalModernization: "Modernização Arquitetural",
        },

        // Metrics
        metrics: {
          fasterDelivery: "3x Entrega Mais Rápida",
          debtReduction: "90% Redução de Dívida",
          enterprises: "50+ Empresas",
        },

        // CTA Section
        cta: {
          title: "Pronto para Transformar Sua Organização de Engenharia?",
          description:
            "Agende uma chamada de descoberta para discutir seus desafios específicos e saiba como nossa abordagem incorporada pode acelerar a transformação de sua equipe.",
          scheduleCall: "Agendar Chamada de Descoberta",
          startDiscovery: "Iniciar Processo de Descoberta",
        },
      },

      // Service Ecosystem
      serviceEcosystem: {
        title: "Serviços Especializados de Transformação de Engenharia",
        subtitle: "Ecossistema de Serviços",
        description:
          "Três áreas de serviço focadas projetadas para transformar sua organização de engenharia de dentro para fora. Cada serviço incorpora nossos especialistas diretamente com suas equipes para impulsionar mudanças mensuráveis e sustentáveis.",

        stats: {
          teamsTransformed: "Equipes Transformadas",
          velocityIncrease: "Aumento Médio de Velocidade",
          paybackPeriod: "Período Médio de Retorno",
        },

        tabs: {
          servicesOverview: "Visão Geral dos Serviços",
          getRecommendations: "Obter Recomendações",
          technologyStack: "Stack de Tecnologia",
          roiCalculator: "Calculadora de ROI",
        },

        services: {
          velocityEngineering: {
            title: "Engenharia de Velocidade",
            subtitle: "Aceleração Sprint-para-Produção",
            description:
              "Transforme seu processo de desenvolvimento de cerimônias de planejamento de sprint em funcionalidades prontas para produção. Nossos engenheiros incorporados trabalham diretamente com suas equipes para eliminar gargalos, reduzir dívida técnica e estabelecer padrões de entrega sustentáveis que se acumulam ao longo do tempo.",
            badge: "Mais Popular",
            focusArea: "Velocidade e Qualidade de Desenvolvimento",
            avgImpact: "35% de entrega mais rápida",
          },
          ownershipCulture: {
            title: "Transformação de Cultura de Propriedade",
            subtitle: "Sistemas de Responsabilidade e Dinâmicas de Equipe",
            description:
              "Vá além das cerimônias ágeis para criar uma cultura genuína de propriedade onde as equipes assumem total responsabilidade pelos resultados. Incorporamos agentes de mudança cultural que trabalham com suas equipes para estabelecer práticas sustentáveis que impulsionam tanto produtividade quanto satisfação.",
            badge: "Alto Impacto",
            focusArea: "Cultura de Equipe e Responsabilidade",
            avgImpact: "25% de ganho de produtividade",
          },
          architecturalModernization: {
            title: "Modernização Arquitetural",
            subtitle: "Escalabilidade e Evolução do Sistema",
            description:
              "Modernize sua arquitetura para escala e manutenibilidade sem interromper as operações atuais. Nossos arquitetos principais trabalham incorporados com suas equipes para projetar e implementar padrões de arquitetura evolutiva que suportam tanto as necessidades atuais quanto o crescimento futuro.",
            badge: "Empresarial",
            focusArea: "Arquitetura e Escalabilidade",
            avgImpact: "40% de melhoria de performance",
          },
        },

        cta: {
          title: "Pronto para Transformar Sua Organização de Engenharia?",
          description:
            "Agende uma chamada de descoberta para discutir seus desafios específicos e saiba como nossa abordagem incorporada pode acelerar a transformação de sua equipe.",
          scheduleCall: "Agendar Chamada de Descoberta",
          getRecommendations: "Obter Recomendações de Serviço",
        },
      },

      // Discovery Portal
      discoveryPortal: {
        title: "Portal de Descoberta",
        subtitle:
          "Transforme sua organização de engenharia com nosso sistema inteligente de qualificação",
        description:
          "Transforme sua organização de engenharia com nosso sistema inteligente de qualificação. Obtenha recomendações personalizadas e conecte-se com a expertise certa para seus desafios.",

        valueProps: {
          intelligentAssessment: {
            title: "Avaliação Inteligente",
            description:
              "Nossa avaliação abrangente identifica seus desafios específicos e oportunidades de melhoria.",
          },
          personalizedRecommendations: {
            title: "Recomendações Personalizadas",
            description:
              "Receba roteiros de transformação customizados baseados em seu ambiente técnico único e objetivos.",
          },
          expertConsultation: {
            title: "Consultoria Especializada",
            description:
              "Conecte-se com engenheiros principais que se especializam em seus desafios específicos e indústria.",
          },
        },

        journey: {
          title: "Sua Jornada de Descoberta",
          steps: {
            assessment: {
              title: "Avaliação",
              description: "Complete nossa avaliação inteligente de desafios",
            },
            recommendations: {
              title: "Recomendações",
              description: "Receba roteiro de transformação personalizado",
            },
            qualification: {
              title: "Qualificação",
              description: "Forneça informações detalhadas do ambiente técnico",
            },
            discoveryCall: {
              title: "Chamada de Descoberta",
              description: "Agende consultoria com nossos especialistas",
            },
          },
        },

        cta: {
          startAssessment: "Iniciar Avaliação",
          browseResources: "Navegar Recursos",
        },

        assessment: {
          title: "Avaliação de Desafios de Engenharia",
          description:
            "Ajude-nos a entender sua situação atual para fornecer as recomendações mais relevantes",
        },

        qualification: {
          title: "Formulário de Qualificação",
          description:
            "Forneça informações detalhadas para nos ajudar a preparar para sua chamada de descoberta",
        },

        resources: {
          title: "Biblioteca de Recursos",
          description:
            "Acesse nossa coleção abrangente de recursos de transformação de engenharia",
        },

        trust: {
          title: "Confiança e Segurança",
          description:
            "Sua segurança e confidencialidade são nossas principais prioridades",
        },

        footer: {
          enterpriseSecurity: "Segurança Empresarial",
          freeDiscoveryCall: "Chamada de Descoberta Gratuita",
          ndaAvailable: "NDA Disponível",
        },
      },

      // Products page
      products: {
        title: "Produtos",
        subtitle:
          "Soluções de software empresarial projetadas para transformação contínua",
        description:
          "Descubra nossos próximos produtos de software que estendem a metodologia de transformação da SIGNA em plataformas empresariais poderosas.",

        // Enterprise Server Management
        serverManagement: {
          title: "Gerenciamento de Servidores Empresariais",
          subtitle: "Automação de Infraestrutura e Monitoramento",
          description:
            "Gerenciamento abrangente de infraestrutura de servidores com pipelines de implantação automatizados, monitoramento em tempo real e capacidades de manutenção preditiva.",
          features: [
            "Orquestração de implantação automatizada",
            "Monitoramento de desempenho em tempo real",
            "Alertas de manutenção preditiva",
            "Suporte a infraestrutura multi-cloud",
            "Automação de conformidade de segurança",
          ],
        },

        // Custom Integrations
        customIntegrations: {
          title: "Integrações Personalizadas",
          subtitle: "Orquestração de API e Pipelines de Dados",
          description:
            "Plataforma de integração perfeita para conectar sistemas empresariais com pipelines de dados automatizados e sincronização em tempo real.",
          features: [
            "Framework de orquestração de API",
            "Sincronização de dados em tempo real",
            "Desenvolvimento de conectores personalizados",
            "Integração de sistemas empresariais",
            "Pipelines de transformação de dados",
          ],
        },

        // No-Code UI Platform
        noCodePlatform: {
          title: "Plataforma de UI Sem Código",
          subtitle: "Desenvolvimento Visual de Aplicações",
          description:
            "Construtor de interface arrastar e soltar para criar aplicações empresariais sem requisitos de codificação tradicionais.",
          features: [
            "Construtor visual de aplicações",
            "Biblioteca de componentes pré-construídos",
            "Ferramentas de automação de fluxo de trabalho",
            "Integração com banco de dados",
            "Design responsivo para mobile",
          ],
        },

        // Early Access
        earlyAccess: {
          title: "Obter Acesso Antecipado",
          description:
            "Esteja entre os primeiros a experimentar as soluções de software empresarial da SIGNA.",
          emailPlaceholder: "Digite seu endereço de e-mail",
          companyPlaceholder: "Nome da empresa",
          submit: "Solicitar Acesso Antecipado",
        },
      },

      // Footer
      footer: {
        description:
          "Transformando organizações de engenharia através de expertise incorporada, cultura de propriedade e metodologias focadas em velocidade.",
        services: "Serviços",
        company: "Empresa",
        velocityEngineering: "Engenharia de Velocidade",
        ownershipCulture: "Cultura de Propriedade",
        architecturalModernization: "Modernização Arquitetural",
        enterpriseConsulting: "Consultoria Empresarial",
        aboutUs: "Sobre Nós",
        caseStudies: "Casos de Estudo",
        knowledgeHub: "Centro de Conhecimento",
        contact: "Contato",
        allRightsReserved: "Todos os direitos reservados",
      },
    },
  },
};

i18n.use(initReactI18next).init({
  resources,
  lng: "en",
  fallbackLng: "en",
  interpolation: {
    escapeValue: false,
  },
});

export default i18n;
